<template>
  <div class="mx-auto">
    <ditto-form
      :dark="true"
      :fields="fields"
      :fields-style="{ 'flex-basis': '100%' }"
      :loading="loading"
      submit-label="login"
      v-model="form"
      @submit="submit"
    >
      <template v-slot:header>
        <div v-if="title !== ''">
          <h2 class="text-uppercase primary--text my-4">
            {{ $t(title) }}
          </h2>
        </div>

        <div>
          <template v-if="localAlertMessage || error">
            <v-alert
              v-if="localAlertMessage"
              close-icon="mdi-close"
              dense
              dismissible
              outlined
              :style="{ lineHeight: 1.25 }"
              :type="alertType"
              @input="dismissAlert"
            >
              {{ $t(localAlertMessage) }}
            </v-alert>

            <v-alert v-if="error" dense outlined type="error">
              <span v-html="error" /><br /><br /><a
                v-if="error"
                href="#"
                @click="resendActivation"
                >Resend Activation Email</a
              >
            </v-alert>
          </template>
        </div>
      </template>

      <template v-slot:footer>
        <div :style="{ 'flex-basis': '100%' }">
          <google-login-button
            text="signin_with"
            class="mb-4"
            @credential="googleLogin"
            @error="err => (error = err)"
          />
          <div class="d-flex justify-space-between">
            <div v-if="allowPasswordReset && showDemoButton">
              <a :href="demoMagicLoginUrl"><b>SEE THE DEMO</b></a>
            </div>
            <div v-if="allowPasswordReset">
              <a @click="resetPassword"><b>Forgot Password?</b></a>
            </div>
            <div v-if="allowUserRegistration">
              New here?
              <a @click="register">Sign up</a>.
            </div>
          </div>
        </div>
      </template>
    </ditto-form>
  </div>
</template>

<script>
import Form from "@/plugins/form/Form.vue";
import GoogleLoginButton from "./GoogleLoginButton.vue";
import { customizeRules } from "../../form/rules";

// Skip email/password default validation
customizeRules({
  emailValidationRegex: new RegExp(".*"),
  passwordValidationRegex: new RegExp(".*")
});

export default {
  name: "LoginForm",
  components: { DittoForm: Form, GoogleLoginButton },
  props: {
    alertMessage: {
      // Keep original prop to avoid conflicts
      required: false,
      type: String
    },
    alertType: { default: "success", type: String },
    allowPasswordReset: { default: true, type: Boolean },
    allowUserRegistration: { default: true, type: Boolean },
    baseRoute: { default: "", type: String },
    title: { default: "login", type: String },
    dark: { type: Boolean, default: false }
  },
  data: () => ({
    loading: false,
    error: null,
    localAlertMessage: "", // Stores activation confirmation or failure message
    fields: [
      { label: "email", key: "email", required: () => true, type: "email" },
      {
        component: "PasswordField",
        label: "password",
        key: "password",
        required: () => true,
        type: "password"
      }
    ],
    form: { email: "", password: "" }
  }),

  mounted() {
    this.$store.commit("auth/reset");
    if (this.$route.query.email) {
      this.form.email = this.$route.query.email;
    }
    console.log(this.form.email);
    // Use Vue Router query parameters for activation messages
    if (this.$route.query.status === "success") {
      this.localAlertMessage = "Your account has been successfully activated!";
    } else if (this.$route.query.status === "error") {
      this.error =
        this.$route.query.message || "Activation failed. Please try again.";
    }

    // If `alertMessage` prop exists, use it (for backward compatibility)
    if (this.alertMessage) {
      this.localAlertMessage = this.alertMessage;
    }
  },
  computed: {
    showDemoButton() {
      return Boolean(this.demoBridgeBaseUrl);
    },
    demoPreviewRequested() {
      return String(this.$route?.query?.demo_preview || "").trim() === "1";
    },
    isLocalhost() {
      if (typeof window === "undefined") return false;
      return ["localhost", "127.0.0.1", "::1"].includes(window.location.hostname);
    },
    demoBridgeBaseUrl() {
      const configuredBridge = (process.env.VUE_APP_DEMO_MAGIC_BRIDGE_URL || "")
        .trim()
        .replace(/\/+$/, "");
      if (configuredBridge) return configuredBridge;

      // Local preview helper: emulate DEV demo button without changing env files.
      if (this.isLocalhost && this.demoPreviewRequested) {
        return "https://calm-paper-f8a0.daniele-ciriello.workers.dev";
      }

      return "";
    },
    demoMagicKey() {
      return (process.env.VUE_APP_DEMO_MAGIC_KEY || "base").trim() || "base";
    },
    demoMagicNextPath() {
      const raw = (process.env.VUE_APP_DEMO_MAGIC_NEXT || "/dashboard/").trim();
      return raw.startsWith("/") && !raw.startsWith("//") ? raw : "/dashboard/";
    },
    demoMagicLoginUrl() {
      if (!this.demoBridgeBaseUrl) return "#";
      return `${this.demoBridgeBaseUrl}/?demo_key=${encodeURIComponent(
        this.demoMagicKey
      )}&next=${encodeURIComponent(this.demoMagicNextPath)}`;
    }
  },

  methods: {
    googleLogin(credential) {
      this.error = null;
      this.loading = true;
      this.$store
        .dispatch("auth/googleLogin", { credential })
        .then(() => this.$router.replace(`${this.baseRoute}/`))
        .catch(error => {
          this.error =
            (error.body && error.body.detail) || "Google login failed";
        })
        .finally(() => (this.loading = false));
    },
    dismissAlert() {
      this.localAlertMessage = "";
      this.error = "";
      this.$router.replace({ query: {} });
    },
    submit() {
      this.error = null;
      this.loading = true;

      this.$store
        .dispatch("auth/login", this.form)
        .then(() => this.$router.replace(`${this.baseRoute}/`))
        .catch(error => {
          let details = "";
          if (error.body) {
            if (error.body.email || error.body.password) {
              if (error.body.email) {
                details += `${this.$t("email")}:<br />`;
                details += error.body.email.join("<br />");
              }
              if (error.body.password) {
                error.body.email ? (details += "<br /><br />") : null;
                details += `${this.$t("password")}:<br />`;
                details += error.body.password.join("<br />");
              }
            } else {
              details =
                error.body.detail ||
                `ERROR ${error.status} - ${error.statusText} ${error.body}`;
            }
          } else {
            details = error;
          }
          this.error = details;
        })
        .finally(() => (this.loading = false));
    },
    resendActivation() {
      this.$store
        .dispatch("auth/resendActivation", { email: this.form.email })
        .then(() => {
          this.localAlertMessage =
            "A new activation email has been sent to your email address.";
          this.error = null;
        })
        .catch(() => {
          this.error =
            "Failed to send activation email. Please try again later.";
        })
        .finally();
    },
    register() {
      this.$router.replace({ name: "register" });
    },
    resetPassword() {
      this.$router.replace({
        name: "forgot-password",
        query: { email: this.form.email }
      });
    }
  }
};
</script>

<style scoped src="./style.css"></style>
